import { useCallback } from 'react';
import { SprintAPI } from '../../utils/api';

/**
 * Hook for reordering nested campaigns within their parent's childOrder.
 * 
 * This is called when dragging a nested campaign above/below a sibling
 * in the sidebar. Uses optimistic updates for snappy UI feel.
 */
export const useReorderNestedCampaign = (setSprints) => {
  /**
   * Reorder a nested campaign within its parent's childOrder.
   * 
   * @param {string} draggedId - ID of the campaign being dragged
   * @param {string} targetId - ID of the sibling campaign to position relative to
   * @param {string} position - 'before' or 'after' the target
   * @param {string} parentId - ID of the parent campaign containing both
   * @param {string[]} currentVisibleOrder - The current order of siblings as displayed in the UI
   */
  const reorderNestedCampaign = useCallback(async (draggedId, targetId, position, parentId, currentVisibleOrder) => {
    console.log('[REORDER_NESTED] Input:', { draggedId, targetId, position, parentId, currentVisibleOrder });
    if (!parentId || draggedId === targetId) return;
    
    // Use the current visible order from the UI as the base
    let currentOrder = currentVisibleOrder || [];
    
    // Ensure both dragged and target are in the order
    if (!currentOrder.includes(draggedId)) {
      currentOrder = [...currentOrder, draggedId];
    }
    if (!currentOrder.includes(targetId)) {
      currentOrder = [...currentOrder, targetId];
    }
    
    // Remove dragged from current position
    const newOrder = currentOrder.filter(id => id !== draggedId);
    
    // Find target index in the new array
    const targetIndex = newOrder.indexOf(targetId);
    
    // Insert at the correct position
    if (position === 'after') {
      newOrder.splice(targetIndex + 1, 0, draggedId);
    } else {
      newOrder.splice(targetIndex, 0, draggedId);
    }
    
    // ═══════════════════════════════════════════════════════════════
    // OPTIMISTIC UPDATE - Update UI immediately for snappy feel
    // ═══════════════════════════════════════════════════════════════
    setSprints(prev => prev.map(s => 
      s.id === parentId ? { ...s, childOrder: newOrder } : s
    ));
    
    // ═══════════════════════════════════════════════════════════════
    // BACKGROUND SAVE - Save to API without blocking UI
    // ═══════════════════════════════════════════════════════════════
    (async () => {
      try {
        // Fetch fresh parent data to merge with
        const parentSprint = await SprintAPI.get(parentId);
        if (parentSprint) {
          console.log('[REORDER_NESTED] Saving childOrder to API:', newOrder);
          const result = await SprintAPI.update(parentId, {
            ...parentSprint,
            childOrder: newOrder,
          });
          console.log('[REORDER_NESTED] API save result:', result);
          console.log('[REORDER_NESTED] Saved childOrder:', result?.childOrder);
          console.log('[REORDER_NESTED] All keys in result:', Object.keys(result || {}));
        } else {
          console.error('[REORDER_NESTED] Parent sprint not found:', parentId);
        }
      } catch (error) {
        console.error('[REORDER_NESTED] Failed to save:', error);
        // Could revert optimistic update here, but usually not needed
      }
    })();
  }, [setSprints]);

  return { reorderNestedCampaign };
};

export default useReorderNestedCampaign;
